<template>
  <Teleport to="body">
    <div
      v-if="show"
      :class="['consultant-bill-details-modal-overlay', { 'consultant-bill-details-modal-overlay--stacked': stacked }]"
    >
      <div
        class="consultant-bill-details-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consultant-bill-details-modal-dialog-title"
        title="Drag the bottom-right corner to resize"
        @click.stop
      >
        <header
          class="consultant-bill-details-modal-header"
          :class="{ 'consultant-bill-details-modal-header--with-back': showBack }"
        >
          <div v-if="showBack" class="consultant-bill-details-modal-header-toolbar">
            <button
              type="button"
              class="consultant-bill-details-modal-back"
              :aria-label="backLabel"
              :title="backLabel"
              @click="handleBack"
            >
              <i class="bi bi-arrow-left" aria-hidden="true"></i>
              <span>{{ backLabel }}</span>
            </button>
            <button
              type="button"
              class="consultant-bill-details-modal-close"
              aria-label="Close"
              title="Close"
              @click="handleClose"
            >
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </div>
          <div class="consultant-bill-details-modal-heading">
            <h2 id="consultant-bill-details-modal-dialog-title" class="consultant-bill-details-modal-sr-only">
              {{ accessibilityTitle }}
            </h2>
            <div class="consultant-bill-details-modal-row-primary-wrap">
              <div class="consultant-bill-details-modal-row-primary">
                  <span v-if="displayVendor" class="consultant-bill-details-modal-customer-name">{{ displayVendor }}</span>
                  <template v-if="displayVendor && billTitleTrim">
                    <span class="consultant-bill-details-modal-primary-sep" aria-hidden="true">—</span>
                  </template>
                  <span v-if="billTitleTrim" class="consultant-bill-details-modal-invoice-no">
                    Bill: <strong>{{ billTitleTrim }}</strong>
                  </span>
                  <span v-else-if="!displayVendor" class="consultant-bill-details-modal-invoice-no">Bill details</span>
                <div
                  v-if="headerMetaChips.length"
                  class="consultant-bill-details-modal-primary-meta"
                >
                  <div
                    v-for="(chip, idx) in headerMetaChips"
                    :key="'bm-' + idx"
                    class="consultant-bill-details-modal-detail-chip"
                  >
                    <span class="consultant-bill-details-modal-detail-k">{{ chip.label }}</span>
                    <span class="consultant-bill-details-modal-detail-v">{{ chip.value }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="moneySummary"
              class="consultant-bill-details-modal-row-details-wrap"
            >
              <div class="consultant-bill-details-modal-row-details">
                <div
                  v-if="hasMoney(moneySummary.total)"
                  class="consultant-bill-details-modal-money consultant-bill-details-modal-money--inline consultant-bill-details-modal-money--emph"
                >
                  <span>Bill total</span>
                  <span>{{ formatCurrency(moneySummary.total) }}</span>
                </div>
                <div
                  v-if="hasMoney(moneySummary.paid)"
                  class="consultant-bill-details-modal-money consultant-bill-details-modal-money--inline"
                >
                  <span>Paid</span>
                  <span>{{ formatCurrency(moneySummary.paid) }}</span>
                </div>
                <div
                  v-if="hasMoney(moneySummary.balance)"
                  class="consultant-bill-details-modal-money consultant-bill-details-modal-money--inline consultant-bill-details-modal-money--balance"
                >
                  <span>Balance</span>
                  <span>{{ formatCurrency(moneySummary.balance) }}</span>
                </div>
                <button
                  v-if="billMemo"
                  type="button"
                  class="consultant-bill-details-modal-memo-btn"
                  :aria-expanded="memoOpen ? 'true' : 'false'"
                  @click="memoOpen = !memoOpen"
                >
                  Memo
                </button>
                <div
                  v-if="showConsultantLineStatus"
                  class="consultant-bill-details-modal-money consultant-bill-details-modal-money--inline consultant-bill-details-modal-money--rp-status"
                >
                  <span>Your line status</span>
                  <span>{{ formatStatus(consultantRecordStatusTrim) }}</span>
                </div>
              </div>
            </div>
          </div>
          <button
            v-if="!showBack"
            type="button"
            class="consultant-bill-details-modal-close"
            aria-label="Close"
            title="Close"
            @click="handleClose"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>

        <div class="consultant-bill-details-modal-body">
          <div v-if="billLoading" class="consultant-bill-details-modal-empty consultant-bill-details-modal-empty--compact">
            Loading bill…
          </div>
          <div v-else-if="billError" class="consultant-bill-details-modal-empty consultant-bill-details-modal-empty--compact">
            <p class="consultant-bill-details-modal-je-error">{{ billError }}</p>
            <button type="button" class="consultant-bill-details-modal-je-retry" @click="loadBillDetail">
              Retry
            </button>
          </div>
          <template v-else-if="billDetail">
            <section
              v-show="memoOpen && billMemo"
              class="consultant-bill-details-modal-summary consultant-bill-details-modal-summary--notes-only"
            >
              <div class="consultant-bill-details-modal-extra">
                <div class="consultant-bill-details-modal-extra-line consultant-bill-details-modal-extra-line--memo">
                  <span class="consultant-bill-details-modal-extra-k">Memo</span>
                  <span class="consultant-bill-details-modal-extra-v">{{ billMemo }}</span>
                </div>
              </div>
            </section>

            <nav
              v-if="canViewJournalEntries"
              class="consultant-bill-details-modal-tabs"
              role="tablist"
              aria-label="Bill details"
            >
              <button
                type="button"
                role="tab"
                class="consultant-bill-details-modal-tab"
                :class="{ 'consultant-bill-details-modal-tab--active': bodyView === 'lines' }"
                :aria-selected="bodyView === 'lines' ? 'true' : 'false'"
                @click="switchBodyTab('lines')"
              >
                Bill lines
              </button>
              <button
                type="button"
                role="tab"
                class="consultant-bill-details-modal-tab"
                :class="{ 'consultant-bill-details-modal-tab--active': bodyView === 'journal' }"
                :aria-selected="bodyView === 'journal' ? 'true' : 'false'"
                @click="switchBodyTab('journal')"
              >
                Journal entries
              </button>
            </nav>

            <div class="consultant-bill-details-modal-tab-panel" role="tabpanel">
            <template v-if="bodyView === 'lines'">
              <div
                v-if="!hasAnyBillLines"
                class="consultant-bill-details-modal-empty"
              >
                No line items on this bill.
              </div>
              <div v-else class="consultant-bill-details-modal-table-scroll">
                <div
                  v-if="manualAccountRows.length"
                  class="consultant-bill-details-modal-table-block"
                >
                <table
                  class="consultant-bill-details-modal-table consultant-bill-details-modal-table--receipt"
                >
                  <thead>
                    <tr>
                      <th scope="col" class="consultant-bill-details-modal-th">Account</th>
                      <th scope="col" class="consultant-bill-details-modal-th">Description</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Amount</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Paid</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in manualAccountRows"
                      :key="'ba-' + row.id"
                      class="consultant-bill-details-modal-tr"
                    >
                      <td class="consultant-bill-details-modal-td">{{ row.accountName }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--desc">{{ row.description || '—' }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num">{{ formatCurrency(row.amount) }}</td>
                      <td
                        class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num consultant-bill-details-modal-td--rp-applied"
                      >{{ formatCurrency(row.paidAmount) }}</td>
                      <td
                        class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num consultant-bill-details-modal-td--balance"
                      >{{ formatCurrency(row.balanceAmount) }}</td>
                    </tr>
                  </tbody>
                </table>
                </div>

                <div
                  v-if="consultantRows.length"
                  class="consultant-bill-details-modal-table-block"
                >
                <table
                  class="consultant-bill-details-modal-table consultant-bill-details-modal-table--receipt"
                >
                  <thead>
                    <tr>
                      <th scope="col" class="consultant-bill-details-modal-th">Consultant</th>
                      <th scope="col" class="consultant-bill-details-modal-th">Description</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Hours</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Rate</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Total</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Paid</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in consultantRows"
                      :key="'bc-' + row.id"
                      class="consultant-bill-details-modal-tr"
                      :class="{ 'consultant-bill-details-modal-tr--highlight': isHighlightedConsultantRow(row) }"
                    >
                      <td class="consultant-bill-details-modal-td">{{ row.consultantName || '—' }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--desc">{{ row.description || '—' }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num">{{ formatQty(row.hours) }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num">{{ formatCurrency(row.hourlyRate) }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num">{{ formatCurrency(row.total) }}</td>
                      <td
                        class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num consultant-bill-details-modal-td--rp-applied"
                      >{{ formatCurrency(row.paidAmount) }}</td>
                      <td
                        class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num consultant-bill-details-modal-td--balance"
                      >{{ formatCurrency(row.balanceAmount) }}</td>
                    </tr>
                  </tbody>
                </table>
                </div>

                <div
                  v-if="itemRows.length"
                  class="consultant-bill-details-modal-table-block"
                >
                <table
                  class="consultant-bill-details-modal-table consultant-bill-details-modal-table--receipt"
                >
                  <thead>
                    <tr>
                      <th scope="col" class="consultant-bill-details-modal-th">Item / service</th>
                      <th scope="col" class="consultant-bill-details-modal-th">Description</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Qty</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Unit price</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Total</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Paid</th>
                      <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in itemRows" :key="'bi-' + row.id" class="consultant-bill-details-modal-tr">
                      <td class="consultant-bill-details-modal-td">{{ row.itemName || '—' }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--desc">{{ row.description || '—' }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num">{{ formatQty(row.quantity) }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num">{{ formatCurrency(row.unitPrice) }}</td>
                      <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num">{{ formatCurrency(row.total) }}</td>
                      <td
                        class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num consultant-bill-details-modal-td--rp-applied"
                      >{{ formatCurrency(row.paidAmount) }}</td>
                      <td
                        class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num consultant-bill-details-modal-td--balance"
                      >{{ formatCurrency(row.balanceAmount) }}</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
            </template>

            <div v-else class="consultant-bill-details-modal-table-scroll">
              <div v-if="journalLoading" class="consultant-bill-details-modal-empty consultant-bill-details-modal-empty--compact">
                Loading journal entries…
              </div>
              <div v-else-if="journalError" class="consultant-bill-details-modal-empty consultant-bill-details-modal-empty--compact">
                <p class="consultant-bill-details-modal-je-error">{{ journalError }}</p>
                <button type="button" class="consultant-bill-details-modal-je-retry" @click="loadJournalEntriesForBill">
                  Retry
                </button>
              </div>
              <div v-else-if="!flattenedJournalRows.length" class="consultant-bill-details-modal-empty consultant-bill-details-modal-empty--compact">
                No journal entries linked to this bill.
              </div>
              <table v-else class="consultant-bill-details-modal-table consultant-bill-details-modal-table--journal">
                <thead>
                  <tr>
                    <th scope="col" class="consultant-bill-details-modal-th">Entry #</th>
                    <th scope="col" class="consultant-bill-details-modal-th">Date</th>
                    <th scope="col" class="consultant-bill-details-modal-th">Status</th>
                    <th scope="col" class="consultant-bill-details-modal-th">Account</th>
                    <th scope="col" class="consultant-bill-details-modal-th">Memo</th>
                    <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Debit</th>
                    <th scope="col" class="consultant-bill-details-modal-th consultant-bill-details-modal-th--num">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in flattenedJournalRows" :key="row.rowKey" class="consultant-bill-details-modal-tr">
                    <td class="consultant-bill-details-modal-td">{{ row.entryNo || '—' }}</td>
                    <td class="consultant-bill-details-modal-td">{{ formatDate(row.entryDate) }}</td>
                    <td class="consultant-bill-details-modal-td">{{ formatStatus(row.status) }}</td>
                    <td class="consultant-bill-details-modal-td">{{ row.accountName }}</td>
                    <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--desc">{{ row.memo }}</td>
                    <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num consultant-bill-details-modal-td--je-debit">
                      {{ formatCurrency(row.debit) }}
                    </td>
                    <td class="consultant-bill-details-modal-td consultant-bill-details-modal-td--num consultant-bill-details-modal-td--je-credit">
                      {{ formatCurrency(row.credit) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import './BillDetailsModal.css';
import ApiService from '@/services/api.service.js';
import JournalEntryService from '@/services/journalEntry.service.js';
import logger from '@/utils/logger';

export default {
  name: 'BillDetailsModal',
  props: {
    show: { type: Boolean, default: false },
    stacked: { type: Boolean, default: false },
    showBack: { type: Boolean, default: false },
    backLabel: { type: String, default: 'Back to pay bill' },
    companyId: { type: [String, Number], default: null },
    enterBillId: { type: [String, Number], default: null },
    billTitle: { type: String, default: '' },
    vendorName: { type: String, default: '' },
    /** Optional: consultant line status from list row (e.g. Bill Paid) */
    consultantRecordStatus: { type: String, default: '' },
    /** When opened from consultant transactions, highlight this consultant bill line */
    highlightConsultantBillRecordId: { type: [String, Number], default: null },
    /** Highlight any consultant line rows for this company consultant (db id) */
    highlightConsultantId: { type: [String, Number], default: null }
  },
  emits: ['close', 'back'],
  data() {
    return {
      billDetail: null,
      billLoading: false,
      billError: '',
      bodyView: 'lines',
      memoOpen: false,
      journalEntries: [],
      journalLoading: false,
      journalError: '',
      journalLoadedKey: null
    };
  },
  computed: {
    billTitleTrim() {
      return (this.billTitle || '').trim();
    },
    displayVendor() {
      const fromBill = this.billDetail?.vendor?.vendorName;
      const fromProp = (this.vendorName || '').trim();
      return (fromBill || fromProp || '').trim();
    },
    accessibilityTitle() {
      const v = this.displayVendor;
      const b = this.billTitleTrim;
      if (v && b) return `${v}, Bill ${b}`;
      if (b) return `Bill ${b}`;
      if (v) return v;
      return 'Bill details';
    },
    canViewJournalEntries() {
      const bid = this.enterBillId;
      const hasBill = bid !== null && bid !== undefined && bid !== '';
      const hasCompany =
        this.companyId !== null && this.companyId !== undefined && this.companyId !== '';
      return hasBill && hasCompany;
    },
    journalCacheKey() {
      return `bill:${this.companyId}:${this.enterBillId}`;
    },
    headerMetaChips() {
      const b = this.billDetail;
      if (!b) return [];
      const chips = [];
      if (b.date) chips.push({ label: 'Bill date', value: this.formatDate(b.date) });
      if (b.dueDate) chips.push({ label: 'Due date', value: this.formatDate(b.dueDate) });
      if (b.status) chips.push({ label: 'Bill status', value: this.formatStatus(b.status) });
      return chips;
    },
    moneySummary() {
      const b = this.billDetail;
      if (!b) return null;
      return {
        total: b.total,
        paid: b.paidAmount,
        balance: b.remainingAmount
      };
    },
    billMemo() {
      const text = this.billDetail?.comments;
      return text && String(text).trim() ? String(text).trim() : '';
    },
    consultantRecordStatusTrim() {
      return (this.consultantRecordStatus || '').trim();
    },
    /** Only when this consultant's line status differs from the vendor bill header status */
    showConsultantLineStatus() {
      const line = this.consultantRecordStatusTrim;
      if (!line) return false;
      const billStatus = this.billDetail?.status;
      if (!billStatus) return false;
      return (
        this.formatStatus(line).toLowerCase() !== this.formatStatus(billStatus).toLowerCase()
      );
    },
    hasAnyBillLines() {
      return (
        this.manualAccountRows.length > 0 ||
        this.consultantRows.length > 0 ||
        this.itemRows.length > 0
      );
    },
    manualAccountRows() {
      const b = this.billDetail;
      if (!b) return [];
      const list = b.accounts || b.accountRecords || [];
      if (!Array.isArray(list)) return [];
      return list
        .filter((row) => row.isManualRecord === true)
        .map((row) => {
          const r = row;
          const amount = Number(r.amount);
          const paid = Number(r.paidAmount || 0);
          const bal = Number.isFinite(amount) ? amount - paid : null;
          let accountName = '—';
          if (r.account) {
            const num = r.account.accountNumber || '';
            const name = r.account.accountName || '';
            accountName = num ? `${num} - ${name}` : name || '—';
          } else if (r.accountName) {
            accountName = r.accountName;
          }
          return {
            id: r.id,
            accountName,
            description: r.description,
            amount: r.amount,
            paidAmount: r.paidAmount,
            balanceAmount: bal
          };
        });
    },
    consultantRows() {
      const b = this.billDetail;
      if (!b) return [];
      const list =
        b.consultants ||
        b.consultantRecords ||
        b.consultantTimesheetRecords ||
        [];
      if (!Array.isArray(list)) return [];
      return list.map((row) => {
        const r = row;
        const total = Number(r.total);
        const paid = Number(r.paidAmount || 0);
        const bal = Number.isFinite(total) ? total - paid : null;
        return {
          id: r.id,
          consultantId: r.consultantId,
          consultantName: r.consultantName,
          description: r.description,
          hours: r.hours,
          hourlyRate: r.hourlyRate,
          total: r.total,
          paidAmount: r.paidAmount,
          balanceAmount: bal
        };
      });
    },
    itemRows() {
      const b = this.billDetail;
      if (!b) return [];
      const list = b.items || b.itemRecords || [];
      if (!Array.isArray(list)) return [];
      return list.map((row) => {
        const r = row;
        const total = Number(r.total);
        const paid = Number(r.paidAmount || 0);
        const bal = Number.isFinite(total) ? total - paid : null;
        const itemName =
          r.itemName ||
          r.productName ||
          r.serviceName ||
          r.productItem?.productName ||
          r.serviceItem?.serviceName ||
          null;
        return {
          id: r.id,
          itemName,
          itemType: r.itemType || (r.productName ? 'Product' : r.serviceName ? 'Service' : null),
          description: r.description,
          quantity: r.quantity,
          unitPrice: r.unitPrice,
          total: r.total,
          paidAmount: r.paidAmount,
          balanceAmount: bal
        };
      });
    },
    flattenedJournalRows() {
      const entries = this.journalEntries;
      if (!Array.isArray(entries) || entries.length === 0) return [];
      const rows = [];
      for (const entry of entries) {
        const base = {
          entryNo: entry.entryNo,
          entryDate: entry.entryDate,
          status: entry.status
        };
        const entryLines = Array.isArray(entry.entryLines) ? entry.entryLines : [];
        if (entryLines.length === 0) {
          rows.push({
            ...base,
            accountName: '—',
            memo: entry.comments ? String(entry.comments) : '—',
            debit: null,
            credit: null,
            rowKey: `je-${entry.id}-empty`
          });
        } else {
          for (const line of entryLines) {
            const lid = line.id != null ? line.id : `${line.accountId}-${rows.length}`;
            rows.push({
              ...base,
              accountName: line.account?.accountName || '—',
              memo: (line.memo && String(line.memo).trim()) ? line.memo : '—',
              debit: line.debit,
              credit: line.credit,
              rowKey: `je-${entry.id}-ln-${lid}`
            });
          }
        }
      }
      return rows;
    }
  },
  watch: {
    show(val) {
      if (!val) {
        this.resetAll();
      } else {
        this.bodyView = 'lines';
        this.memoOpen = false;
        this.journalEntries = [];
        this.journalError = '';
        this.journalLoading = false;
        this.journalLoadedKey = null;
        this.loadBillDetail();
      }
    },
    enterBillId() {
      if (this.show) {
        this.resetAll();
        this.bodyView = 'lines';
        this.loadBillDetail();
      }
    }
  },
  methods: {
    isHighlightedConsultantRow(row) {
      const rid = this.highlightConsultantBillRecordId;
      if (rid !== null && rid !== undefined && rid !== '' && row && row.id != null) {
        if (String(row.id) === String(rid)) return true;
      }
      const cid = this.highlightConsultantId;
      if (cid !== null && cid !== undefined && cid !== '' && row && row.consultantId != null) {
        if (String(row.consultantId) === String(cid)) return true;
      }
      return false;
    },
    resetAll() {
      this.billDetail = null;
      this.billLoading = false;
      this.billError = '';
      this.bodyView = 'lines';
      this.memoOpen = false;
      this.journalEntries = [];
      this.journalError = '';
      this.journalLoading = false;
      this.journalLoadedKey = null;
    },
    async loadBillDetail() {
      const id = this.enterBillId;
      if (id === null || id === undefined || id === '') {
        this.billError = 'Missing bill id.';
        return;
      }
      this.billLoading = true;
      this.billError = '';
      try {
        const data = await ApiService.getEnterBillById(id);
        this.billDetail = data && typeof data === 'object' ? data : null;
        if (!this.billDetail) {
          this.billError = 'Bill not found.';
        }
      } catch (e) {
        logger.error('BillDetailsModal: load bill failed', e);
        this.billError =
          (e && e.response && e.response.data && e.response.data.error) ||
          e.message ||
          'Could not load bill.';
        this.billDetail = null;
      } finally {
        this.billLoading = false;
      }
    },
    async switchBodyTab(view) {
      if (view !== 'lines' && view !== 'journal') return;
      if (this.bodyView === view) return;
      this.bodyView = view;
      if (view === 'journal') {
        await this.loadJournalEntriesForBill();
      }
    },
    async loadJournalEntriesForBill() {
      if (!this.canViewJournalEntries) return;
      const key = this.journalCacheKey;
      if (this.journalLoadedKey === key && !this.journalError) return;
      this.journalLoading = true;
      this.journalError = '';
      try {
        const res = await JournalEntryService.getJournalEntriesByBillId(
          this.enterBillId,
          this.companyId
        );
        const list = res && Array.isArray(res.data) ? res.data : [];
        this.journalEntries = list;
        this.journalLoadedKey = key;
      } catch (e) {
        logger.error('BillDetailsModal: journal fetch failed', e);
        this.journalError =
          (e && e.response && e.response.data && e.response.data.message) ||
          e.message ||
          'Could not load journal entries.';
        this.journalEntries = [];
      } finally {
        this.journalLoading = false;
      }
    },
    handleClose() {
      this.$emit('close');
    },
    handleBack() {
      this.$emit('back');
    },
    hasMoney(v) {
      if (v === undefined || v === null || v === '') return false;
      const n = Number(v);
      return !Number.isNaN(n);
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    },
    formatStatus(status) {
      if (!status) return '—';
      return String(status)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatCurrency(amount) {
      if (amount === undefined || amount === null || amount === '') return '—';
      const n = Number(amount);
      if (Number.isNaN(n)) return '—';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(n);
    },
    formatQty(value) {
      if (value === undefined || value === null || value === '') return '—';
      const n = Number(value);
      if (Number.isNaN(n)) return String(value);
      return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
  }
};
</script>
